import moment from "moment";

/**
 * Shipment progress status shown in the Logistics listing tables.
 *
 * Export dispatches (distribution channel "02"): In Transit / Nearing ETA / Overdue / Delivered.
 * Domestic dispatches (distribution channel "01"): On Track / Overdue / Delivered.
 */
export const SHIPMENT_PROGRESS_STATUS = {
  IN_TRANSIT: "inTransit",
  NEARING_ETA: "nearingEta",
  ON_TRACK: "onTrack",
  OVERDUE: "overdue",
  DELIVERED: "delivered",
};

/** Which listing tab the row is rendered in — decides which milestones apply. */
export const SHIPMENT_PROGRESS_TAB = {
  IN_TRANSIT: "intransit",
  CUSTOMER_DELIVERY_PENDING: "customerDeliveryPending",
  DELIVERED: "delivered",
};

/** Nearing-ETA window: final 15% of planned transit, or the last 2 days, whichever is longer. */
const NEARING_ETA_RATIO = 0.15;
const NEARING_ETA_MIN_DAYS = 2;

/** Each domestic leg (Dispatch -> LR, LR -> Delivery) has a 1 day SLA. */
const DOMESTIC_LEG_SLA_DAYS = 1;

const ACCEPTED_DATE_FORMATS = [
  moment.ISO_8601,
  "MM/DD/YYYY",
  "M/D/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
];

const BLANK_VALUES = new Set(["", "n.a", "n.a.", "na", "n/a", "null", "undefined", "-"]);

/** Domestic (channel "01") milestones — same field keys as the In-Transit tab. */
const DISPATCH_ACTUAL_KEYS = ["dispatch_Actual"];
// In-Transit / Customer Delivery Pending return actual_BL_LR_Date; Delivered returns bL_LR_Date_Actual.
const LR_ACTUAL_KEYS = ["actual_BL_LR_Date", "bL_LR_Date_Actual"];
const DELIVERY_ACTUAL_KEYS = ["delivery_Date_Actual"];
const DELIVERY_ESTIMATED_KEYS = ["delivery_Date_Estimated"];

/** Export (channel "02") reads only the shipment tracking keys. */
const SHIPMENT_DATE_KEYS = ["shipmentDate"];
const ETA_KEYS = ["eta"];
const ORIGIN_KEYS = ["fromLocation"];
const DESTINATION_KEYS = ["toLocation"];

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  BLANK_VALUES.has(String(value).trim().toLowerCase());

const pickValue = (row, keys) => {
  const key = keys.find((item) => !isBlank(row?.[item]));
  return key ? row[key] : null;
};

const toMoment = (value) => {
  if (isBlank(value)) {
    return null;
  }
  const parsed = moment(value, ACCEPTED_DATE_FORMATS);
  return parsed.isValid() ? parsed : null;
};

const pickDate = (row, keys) => toMoment(pickValue(row, keys));

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const formatShortDate = (value) => (value ? value.format("DD MMM") : "");

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const daysBetween = (from, to) => to.diff(from, "days", true);

/** Position of `now` between the two ends of the current window, as a percentage. */
const elapsedPercent = (start, end, now) => {
  if (!start || !end) {
    return null;
  }
  const span = daysBetween(start, end);
  if (span <= 0) {
    return null;
  }
  return clamp((daysBetween(start, now) / span) * 100, 6, 100);
};

const STATUS_META = {
  [SHIPMENT_PROGRESS_STATUS.IN_TRANSIT]: { label: "In transit", tone: "info" },
  [SHIPMENT_PROGRESS_STATUS.ON_TRACK]: { label: "On track", tone: "info" },
  [SHIPMENT_PROGRESS_STATUS.NEARING_ETA]: { label: "Nearing ETA", tone: "warning" },
  [SHIPMENT_PROGRESS_STATUS.OVERDUE]: { label: "Overdue", tone: "danger" },
  [SHIPMENT_PROGRESS_STATUS.DELIVERED]: { label: "Delivered", tone: "success" },
};

const API_STATUS_MAP = {
  intransit: SHIPMENT_PROGRESS_STATUS.IN_TRANSIT,
  transit: SHIPMENT_PROGRESS_STATUS.IN_TRANSIT,
  ontrack: SHIPMENT_PROGRESS_STATUS.ON_TRACK,
  nearingeta: SHIPMENT_PROGRESS_STATUS.NEARING_ETA,
  nearingtoeta: SHIPMENT_PROGRESS_STATUS.NEARING_ETA,
  overdue: SHIPMENT_PROGRESS_STATUS.OVERDUE,
  delayed: SHIPMENT_PROGRESS_STATUS.OVERDUE,
  delivered: SHIPMENT_PROGRESS_STATUS.DELIVERED,
  journeycompleted: SHIPMENT_PROGRESS_STATUS.DELIVERED,
  completed: SHIPMENT_PROGRESS_STATUS.DELIVERED,
};

/** Channel "02" is an export shipment; everything else follows the domestic leg SLA. */
const isExportRow = (row) => normalize(row?.distribution_Channel) === "02";

/** "Mundra, 13 Jul" when the port is known, otherwise "Dispatched 13 Jul". */
const milestoneLabel = (location, date, fallbackPrefix) => {
  const text = formatShortDate(date);
  if (isBlank(location)) {
    return text ? `${fallbackPrefix} ${text}` : fallbackPrefix;
  }
  const port = String(location).trim();
  return text ? `${port}, ${text}` : port;
};

/** The tracking feed's own percentage wins when supplied. */
const exportPercent = (row, startedOn, eta, now, fallback) => {
  const apiPercent = Number(row.progressPercentage);
  if (Number.isFinite(apiPercent) && apiPercent > 0) {
    return clamp(apiPercent, 6, 100);
  }
  return elapsedPercent(startedOn, eta, now) ?? fallback;
};

const buildExportProgress = (row, now) => {
  const startedOn = pickDate(row, SHIPMENT_DATE_KEYS);
  const origin = pickValue(row, ORIGIN_KEYS);
  const destination = pickValue(row, DESTINATION_KEYS);
  const apiStatus = API_STATUS_MAP[normalize(row.shipmentStatus)];
  const withDestination = (text) =>
    isBlank(destination) ? text : `${String(destination).trim()}, ${text}`;

  // An arrival date that precedes departure is stale source data rather than a
  // late shipment, so it cannot be used as the overdue deadline.
  const rawEta = pickDate(row, ETA_KEYS);
  const eta =
    rawEta && (!startedOn || !rawEta.isBefore(startedOn, "day")) ? rawEta : null;

  if (!startedOn && !rawEta && !apiStatus) {
    return null;
  }

  const startLabel = milestoneLabel(origin, startedOn, "Dispatch pending");

  if (apiStatus === SHIPMENT_PROGRESS_STATUS.DELIVERED) {
    return {
      status: SHIPMENT_PROGRESS_STATUS.DELIVERED,
      percent: 100,
      startLabel,
      endLabel: milestoneLabel(destination, eta, "Delivered"),
    };
  }

  if (!eta) {
    return {
      status: apiStatus || SHIPMENT_PROGRESS_STATUS.IN_TRANSIT,
      percent: exportPercent(row, startedOn, eta, now, 35),
      startLabel,
      endLabel: withDestination("ETA awaited"),
    };
  }

  const endLabel = withDestination(`ETA ${formatShortDate(eta)}`);
  const daysToEta = daysBetween(now, eta);

  if (daysToEta < 0) {
    return {
      status: SHIPMENT_PROGRESS_STATUS.OVERDUE,
      overdueDays: Math.max(1, Math.floor(daysBetween(eta, now))),
      percent: 100,
      startLabel,
      endLabel,
    };
  }

  const plannedTransitDays = startedOn ? daysBetween(startedOn, eta) : 0;
  const nearingWindowDays = Math.max(
    plannedTransitDays > 0 ? plannedTransitDays * NEARING_ETA_RATIO : 0,
    NEARING_ETA_MIN_DAYS
  );
  const isNearing = daysToEta <= nearingWindowDays;

  return {
    status: isNearing
      ? SHIPMENT_PROGRESS_STATUS.NEARING_ETA
      : SHIPMENT_PROGRESS_STATUS.IN_TRANSIT,
    percent: exportPercent(row, startedOn, eta, now, isNearing ? 85 : 40),
    startLabel,
    endLabel,
  };
};

const buildDomesticProgress = (row, now, tab) => {
  // The In-Transit tab has no delivery milestone, and Customer Delivery Pending
  // has already cleared the dispatch leg.
  const dispatchedOn =
    tab === SHIPMENT_PROGRESS_TAB.CUSTOMER_DELIVERY_PENDING
      ? null
      : pickDate(row, DISPATCH_ACTUAL_KEYS);
  const lrOn = pickDate(row, LR_ACTUAL_KEYS);
  const deliveredOn =
    tab === SHIPMENT_PROGRESS_TAB.IN_TRANSIT
      ? null
      : pickDate(row, DELIVERY_ACTUAL_KEYS);

  if (!dispatchedOn && !lrOn) {
    return null;
  }

  const startLabel = lrOn
    ? `LR done ${formatShortDate(lrOn)}`
    : `Dispatched ${formatShortDate(dispatchedOn)}`;

  if (deliveredOn) {
    return {
      status: SHIPMENT_PROGRESS_STATUS.DELIVERED,
      percent: 100,
      startLabel,
      endLabel: `Delivered ${formatShortDate(deliveredOn)}`,
    };
  }

  // Leg 2 is measured against the tentative delivery date; leg 1 against the 1 day LR SLA.
  const startedOn = lrOn || dispatchedOn;
  const dueOn = lrOn
    ? pickDate(row, DELIVERY_ESTIMATED_KEYS) ||
      lrOn.clone().add(DOMESTIC_LEG_SLA_DAYS, "days")
    : dispatchedOn.clone().add(DOMESTIC_LEG_SLA_DAYS, "days");
  const endLabel = lrOn
    ? `Delivery due ${formatShortDate(dueOn)}`
    : `LR due ${formatShortDate(dueOn)}`;

  if (now.isAfter(dueOn, "day")) {
    return {
      status: SHIPMENT_PROGRESS_STATUS.OVERDUE,
      overdueDays: Math.max(1, Math.floor(daysBetween(dueOn, now))),
      percent: 100,
      startLabel,
      endLabel,
    };
  }

  // Domestic On track always sits at the midpoint of the bar (mockup).
  return {
    status: SHIPMENT_PROGRESS_STATUS.ON_TRACK,
    percent: 50,
    startLabel,
    endLabel,
  };
};

/**
 * Builds everything the Shipment progress cell needs for a listing row.
 * Returns null when the row carries no usable milestone dates.
 */
export const getShipmentProgress = (row, { tab, referenceDate } = {}) => {
  if (!row) {
    return null;
  }

  const now = referenceDate ? moment(referenceDate) : moment();
  const isExport = isExportRow(row);
  const progress = isExport
    ? buildExportProgress(row, now)
    : buildDomesticProgress(row, now, tab);

  if (!progress) {
    return null;
  }

  // The API may already resolve the status server side; keep it authoritative when present.
  const apiStatus = API_STATUS_MAP[normalize(row.shipmentStatus)];
  const status = apiStatus || progress.status;
  const apiOverdueDays = Number(row.overdueDays);
  const overdueDays =
    Number.isFinite(apiOverdueDays) && apiOverdueDays > 0
      ? Math.round(apiOverdueDays)
      : progress.overdueDays;

  const meta = STATUS_META[status] || STATUS_META[progress.status];
  const isOverdue = status === SHIPMENT_PROGRESS_STATUS.OVERDUE;
  const isOnTrack = status === SHIPMENT_PROGRESS_STATUS.ON_TRACK;
  // Domestic On track is always rendered at mid-bar, matching the mockups.
  const percent = Math.round(
    isOverdue ? 100 : isOnTrack && !isExport ? 50 : progress.percent
  );

  // Export rows surface the tracking feed's own completion figure.
  const apiPercent = Number(row.progressPercentage);
  const trackedPercent =
    Number.isFinite(apiPercent) && apiPercent > 0
      ? Math.round(clamp(apiPercent, 0, 100))
      : percent;

  return {
    status,
    tone: meta.tone,
    statusLabel:
      isOverdue && overdueDays > 0
        ? `${overdueDays} ${overdueDays === 1 ? "day" : "days"} overdue`
        : meta.label,
    overdueDays: isOverdue ? overdueDays || null : null,
    percent,
    percentLabel: isExport ? `${trackedPercent}%` : null,
    startLabel: progress.startLabel,
    endLabel: progress.endLabel,
  };
};
